import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import { Attachments } from '../components/Attachments';
import { RowActions } from '../components/RowActions';
import { RevealedField, useReveal } from '../components/Controls';
import { dateTime, day, number } from '../lib/format';
import {
  Card, Empty, ErrorNote, Field, Hash, Loading, Modal, PageHead, Pill, useResource, useToast,
} from '../components/ui';
import '../styles/nominations.css';

/**
 * S.14 nominations. Activations: a nominee asks to act for a principal on
 * death or incapacity; the principal is told first, one person verifies the
 * evidence and a second approves. Nominees: who has named whom, masked.
 * Spec: docs/NOMINATION-SPEC.md, "Console screens".
 */

type ActivationStatus = 'notice_period' | 'under_review' | 'verified' | 'approved' | 'rejected' | 'objected' | 'ended' | 'withdrawn';
type NominationStatus = 'registered' | 'revoked' | 'superseded' | 'active' | 'ended';

export interface Activation {
  id: string;
  reference: string;
  principalHash: string;
  nominationId: string | null;
  requestId: string | null;
  trigger: string;
  eventDate: string | null;
  note: string;
  status: ActivationStatus;
  noticeUntil: string | null;
  noticeChannel: string | null;
  noticeSentAt: string | null;
  objectedAt: string | null;
  verifiedBy: string | null;
  verifiedAt: string | null;
  verifyNote: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  decisionNote: string | null;
  rejectReason: string | null;
  endedAt: string | null;
  endedReason: string | null;
  createdAt: string;
  dueBy: string | null;
  relationship: string | null;
}

export interface Nomination {
  id: string;
  principalHash: string;
  nomineeContact: string;
  nomineeContactType: 'email' | 'phone';
  relationship: string;
  rank: number;
  status: NominationStatus;
  source: string;
  registeredAt: string;
  revokedAt: string | null;
}

interface NominationSettings { twoPerson: boolean; decisionDays: number }

export const ACTIVATION_LABEL: Record<ActivationStatus, string> = {
  notice_period: 'Notice period',
  under_review: 'Under review',
  verified: 'Verified',
  approved: 'Approved',
  rejected: 'Rejected',
  objected: 'Objected',
  ended: 'Ended',
  withdrawn: 'Withdrawn',
};
export const ACTIVATION_TONE: Record<ActivationStatus, 'ok' | 'warn' | 'bad' | 'brand' | undefined> = {
  notice_period: undefined, under_review: 'warn', verified: 'warn', approved: 'ok',
  rejected: 'bad', objected: 'bad', ended: undefined, withdrawn: undefined,
};

export const NOMINATION_LABEL: Record<NominationStatus, string> = {
  registered: 'Registered', active: 'Acting', revoked: 'Revoked', superseded: 'Replaced', ended: 'Ended',
};
export const NOMINATION_TONE: Record<NominationStatus, 'ok' | 'brand' | undefined> = {
  registered: 'ok', active: 'brand', revoked: undefined, superseded: undefined, ended: undefined,
};

export const RELATIONSHIP_LABEL: Record<string, string> = {
  spouse: 'Spouse', child: 'Child', parent: 'Parent', sibling: 'Sibling', other: 'Other',
};
export const TRIGGER_LABEL: Record<string, string> = { death: 'Death', incapacity: 'Incapacity' };

type ActivationTab = 'waiting' | 'verified' | 'approved' | 'closed';
const ACTIVATION_TABS: Array<[ActivationTab, string, ActivationStatus[]]> = [
  ['waiting', 'Waiting', ['notice_period', 'under_review']],
  ['verified', 'Verified', ['verified']],
  ['approved', 'Approved', ['approved']],
  ['closed', 'Closed', ['rejected', 'objected', 'ended', 'withdrawn']],
];

type NomineeTab = 'registered' | 'active' | 'ended';
const NOMINEE_TABS: Array<[NomineeTab, string, NominationStatus[]]> = [
  ['registered', 'Registered', ['registered']],
  ['active', 'Acting', ['active']],
  ['ended', 'Ended', ['revoked', 'superseded', 'ended']],
];

const OPEN: ActivationStatus[] = ['notice_period', 'under_review', 'verified'];

type View = 'activations' | 'nominees';
const VIEWS: Array<[View, string]> = [['activations', 'Activations'], ['nominees', 'Nominees']];

/** Today as YYYY-MM-DD in the reader's own zone, to compare with a due date. */
function today(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const isOverdue = (a: Activation) => OPEN.includes(a.status) && !!a.dueBy && a.dueBy < today();

/** Notice column: whether the principal was told, and until when they may object. */
function noticeText(a: Activation, long = false): string {
  if (a.noticeChannel === 'none') return long ? 'Not sent: no channel configured' : 'Not sent';
  if (!a.noticeSentAt) return 'Not sent';
  if (a.noticeUntil && new Date(a.noticeUntil).getTime() > Date.now()) return `Sent until ${day(a.noticeUntil)}`;
  return long ? `Sent ${day(a.noticeSentAt)} · ended ${day(a.noticeUntil)}` : 'Ended';
}

const sentence = (v: string | null | undefined) => {
  if (!v) return '—';
  const s = v.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export default function Nominations() {
  const [params, setParams] = useSearchParams();
  const view: View = params.get('view') === 'nominees' ? 'nominees' : 'activations';
  const go = (v: View) => {
    const next = new URLSearchParams(params);
    next.delete('ref');
    if (v === 'activations') next.delete('view'); else next.set('view', v);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHead title="Nominations" />
      <div className="nm">
        <div className="q-bar">
          <div className="q-views" role="tablist" aria-label="Nominations">
            {VIEWS.map(([k, label]) => (
              <button key={k} role="tab" aria-selected={view === k} className={view === k ? 'on' : ''} onClick={() => go(k)}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {view === 'activations' ? <Activations /> : <Nominees />}
      </div>
    </>
  );
}

/* ------------------------------------------------------------ activations */

function Activations() {
  const me = getUser();
  const canHandle = can(me, 'requests:handle');
  const canApprove = can(me, 'requests:approve_nomination');
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<ActivationTab>('waiting');
  const [reload, setReload] = useState(0);
  const [open, setOpen] = useState<Activation | null>(null);
  const [reasonFor, setReasonFor] = useState<{ row: Activation; kind: 'reject' | 'end' } | null>(null);
  const refresh = () => setReload((n) => n + 1);

  // Every status in one read: the tabs group several statuses and carry counts.
  const data = useResource<{ activations: Activation[]; settings: NominationSettings }>(
    () => api.get('/v1/activations'), [reload],
  );
  const all = data.data?.activations ?? [];
  const settings = data.data?.settings;

  // ?ref=NOM-XXXXXXXX opens that activation (linked from Rights requests and the principal record).
  const ref = params.get('ref');
  useEffect(() => {
    if (!ref || !data.data) return;
    const hit = all.find((a) => a.reference === ref);
    if (hit) {
      setOpen(hit);
      const t = ACTIVATION_TABS.find(([, , s]) => s.includes(hit.status));
      if (t) setTab(t[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, data.data]);

  function close() {
    setOpen(null);
    if (ref) {
      const next = new URLSearchParams(params);
      next.delete('ref');
      setParams(next, { replace: true });
    }
  }

  const statuses = ACTIVATION_TABS.find(([k]) => k === tab)![2];
  const rows = all.filter((a) => statuses.includes(a.status));
  const count = (k: ActivationTab) => all.filter((a) => ACTIVATION_TABS.find(([t]) => t === k)![2].includes(a.status)).length;

  const openCount = all.filter((a) => OPEN.includes(a.status)).length;
  const toApprove = all.filter((a) => a.status === 'verified').length;
  const overdue = all.filter(isOverdue).length;
  const notNotified = all.filter((a) => OPEN.includes(a.status) && a.noticeChannel === 'none').length;
  const acting = all.filter((a) => a.status === 'approved').length;

  const secondPersonNeeded = (a: Activation) => !!settings?.twoPerson && !!me && a.verifiedBy === me.email;

  return (
    <>
      {data.data && (
        <p className="page-line">
          <strong>{number(openCount)}</strong> open · {number(toApprove)} to approve · {number(overdue)} overdue
          · {number(notNotified)} principal not notified · {number(acting)} acting
          · two-person approval {settings?.twoPerson ? 'on' : 'off'}
        </p>
      )}

      <ErrorNote error={data.error} />

      <div className="tabs">
        {ACTIVATION_TABS.map(([k, label]) => {
          const n = k === 'waiting' || k === 'verified' ? count(k) : 0;
          return (
            <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
              {label}
              {n ? <span className="tab-count">{n}</span> : null}
            </button>
          );
        })}
      </div>

      <Card>
        {data.loading && !data.data && <Loading what="Loading activations" />}
        {data.data && rows.length === 0 && <Empty>None.</Empty>}
        {rows.length > 0 && (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Reference</th><th>Received</th><th>Principal</th><th>Trigger</th><th>Notice</th><th>Due</th><th>Status</th><th />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const isOpen = OPEN.includes(a.status);
                  let primary: ReactNode;
                  if (a.status === 'under_review' && canHandle) {
                    primary = <button className="small primary" onClick={() => setOpen(a)}>Verify</button>;
                  } else if (a.status === 'verified' && canApprove) {
                    primary = secondPersonNeeded(a)
                      ? <button className="small" disabled>Needs a second person</button>
                      : <button className="small primary" onClick={() => setOpen(a)}>Approve</button>;
                  }
                  return (
                    <tr key={a.id} onClick={() => setOpen(a)} style={{ cursor: 'pointer' }}>
                      <td><strong className="mono">{a.reference}</strong></td>
                      <td className="small">{day(a.createdAt)}</td>
                      <td>
                        <Link to={`/principals/${a.principalHash}`} onClick={(e) => e.stopPropagation()}><Hash value={a.principalHash} /></Link>
                      </td>
                      <td>{TRIGGER_LABEL[a.trigger] ?? sentence(a.trigger)}</td>
                      <td className={`small${a.noticeChannel === 'none' && isOpen ? ' nm-unsent' : ''}`}>{noticeText(a)}</td>
                      <td className={`small${isOverdue(a) ? ' nm-overdue' : ''}`} title={isOverdue(a) ? 'Overdue' : undefined}>{day(a.dueBy)}</td>
                      <td><Pill tone={ACTIVATION_TONE[a.status]}>{ACTIVATION_LABEL[a.status] ?? sentence(a.status)}</Pill></td>
                      <td className="u-actions" onClick={(e) => e.stopPropagation()}>
                        <RowActions
                          primary={primary}
                          more={[
                            ...(a.status === 'approved' && canApprove
                              ? [{ label: 'End', danger: true, onClick: () => setReasonFor({ row: a, kind: 'end' }) }] : []),
                            ...(isOpen && canHandle
                              ? [{ label: 'Reject', danger: true, onClick: () => setReasonFor({ row: a, kind: 'reject' }) }] : []),
                            { label: 'View', onClick: () => setOpen(a) },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {open && (
        <ActivationDetail
          row={open}
          canHandle={canHandle}
          canApprove={canApprove}
          secondPersonNeeded={secondPersonNeeded(open)}
          onClose={close}
          onUpdated={(updated) => { setOpen(updated); refresh(); }}
          onReject={() => { setReasonFor({ row: open, kind: 'reject' }); close(); }}
          onEnd={() => { setReasonFor({ row: open, kind: 'end' }); close(); }}
        />
      )}

      {reasonFor && (
        <ReasonDialog
          title={`${reasonFor.kind === 'reject' ? 'Reject' : 'End'} · ${reasonFor.row.reference}`}
          action={reasonFor.kind === 'reject' ? 'Reject' : 'End'}
          path={`/v1/activations/${reasonFor.row.id}/${reasonFor.kind}`}
          done={reasonFor.kind === 'reject' ? 'Rejected. Nominee told.' : 'Ended. Nominee sessions closed.'}
          onClose={() => setReasonFor(null)}
          onDone={() => { setReasonFor(null); refresh(); }}
        />
      )}
    </>
  );
}

const CHECKS = [
  'Nominee identity established',
  'Evidence matches the trigger',
  'Date consistent',
  'No objection from the principal',
] as const;

function ActivationDetail({ row: a, canHandle, canApprove, secondPersonNeeded, onClose, onUpdated, onReject, onEnd }: {
  row: Activation;
  canHandle: boolean;
  canApprove: boolean;
  secondPersonNeeded: boolean;
  onClose: () => void;
  onUpdated: (row: Activation) => void;
  onReject: () => void;
  onEnd: () => void;
}) {
  const toast = useToast();
  const [checks, setChecks] = useState<boolean[]>(CHECKS.map(() => false));
  const [note, setNote] = useState('');
  const [early, setEarly] = useState(false);
  const [approveNote, setApproveNote] = useState('');
  const [busy, setBusy] = useState(false);

  const isOpen = OPEN.includes(a.status);
  const verifiable = canHandle && (a.status === 'under_review' || a.status === 'notice_period');
  const inNotice = a.status === 'notice_period';
  const canVerifyNow = verifiable && checks.every(Boolean) && !!note.trim() && (!inNotice || early);
  const approvable = canApprove && a.status === 'verified';
  const unsent = a.noticeChannel === 'none';
  const canApproveNow = approvable && !secondPersonNeeded && (!unsent || !!approveNote.trim());

  async function post(path: string, body: unknown, message: string) {
    setBusy(true);
    try {
      const res = await api.post<{ activation: Activation }>(path, body);
      toast(message, 'ok');
      onUpdated(res.activation);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const verify = () => post(`/v1/activations/${a.id}/verify`, { note: note.trim(), ...(inNotice ? { early: true } : {}) }, 'Verified.');
  const approve = () => post(`/v1/activations/${a.id}/approve`, approveNote.trim() ? { note: approveNote.trim() } : {}, 'Approved. Nominee told.');

  return (
    <Modal
      wide
      title={`${a.reference} · ${TRIGGER_LABEL[a.trigger] ?? sentence(a.trigger)}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Close</button>
          {isOpen && canHandle && <button className="danger" onClick={onReject} disabled={busy}>Reject</button>}
          {a.status === 'approved' && canApprove && <button className="danger" onClick={onEnd} disabled={busy}>End</button>}
          {verifiable && (
            <button className="primary" onClick={verify} disabled={busy || !canVerifyNow}>{busy ? 'Verifying…' : 'Verify'}</button>
          )}
          {approvable && (
            secondPersonNeeded
              ? <button disabled>Needs a second person</button>
              : <button className="primary" onClick={approve} disabled={busy || !canApproveNow}>{busy ? 'Approving…' : 'Approve'}</button>
          )}
        </>
      }
    >
      <dl className="kv">
        <dt>Status</dt><dd><Pill tone={ACTIVATION_TONE[a.status]}>{ACTIVATION_LABEL[a.status] ?? sentence(a.status)}</Pill></dd>
        <dt>Principal</dt><dd><Link to={`/principals/${a.principalHash}`}>Open record</Link> · <Hash value={a.principalHash} head={16} /></dd>
        <dt>Nominee</dt><dd>{a.relationship ? RELATIONSHIP_LABEL[a.relationship] ?? sentence(a.relationship) : '—'}</dd>
        <dt>Received</dt><dd>{dateTime(a.createdAt)}</dd>
        <dt>Due</dt><dd className={isOverdue(a) ? 'nm-overdue' : ''}>{day(a.dueBy)}{isOverdue(a) ? ' · overdue' : ''}</dd>
        <dt>Trigger</dt><dd>{TRIGGER_LABEL[a.trigger] ?? sentence(a.trigger)}</dd>
        <dt>Event date</dt><dd>{day(a.eventDate)}</dd>
        <dt>Note</dt><dd>{a.note || '—'}</dd>
        <dt>Notice</dt><dd className={unsent ? 'nm-unsent' : ''}>{noticeText(a, true)}</dd>
        <dt>Objection</dt><dd>{a.objectedAt ? `Objected ${dateTime(a.objectedAt)}` : 'None'}</dd>
        {a.verifiedAt && <>
          <dt>Verified</dt><dd>{dateTime(a.verifiedAt)} · {a.verifiedBy ?? '—'}</dd>
          <dt>Verification note</dt><dd>{a.verifyNote || '—'}</dd>
        </>}
        {a.status === 'rejected' ? <>
          <dt>Rejected</dt><dd>{dateTime(a.approvedAt)} · {a.approvedBy ?? '—'}</dd>
          <dt>Reason</dt><dd>{sentence(a.rejectReason)}</dd>
        </> : a.approvedAt ? <>
          <dt>Approved</dt><dd>{dateTime(a.approvedAt)} · {a.approvedBy ?? '—'}</dd>
          {a.decisionNote && <><dt>Approval note</dt><dd>{a.decisionNote}</dd></>}
        </> : null}
        {a.endedAt && <>
          <dt>Ended</dt><dd>{dateTime(a.endedAt)}</dd>
          <dt>End reason</dt><dd>{sentence(a.endedReason)}</dd>
        </>}
      </dl>

      <h2 className="section">Evidence files</h2>
      {a.requestId
        ? <Attachments entityType="request" entityId={a.requestId} canAdd={canHandle} />
        : <Empty>No files.</Empty>}

      {verifiable && (
        <>
          <h2 className="section">Verification</h2>
          <div className="nm-checks">
            {CHECKS.map((label, i) => (
              <label key={label} className="nm-check">
                <input type="checkbox" checked={checks[i]} onChange={(e) => setChecks(checks.map((c, j) => (j === i ? e.target.checked : c)))} />
                {label}
              </label>
            ))}
          </div>
          {inNotice && (
            <label className="nm-check nm-early">
              <input type="checkbox" checked={early} onChange={(e) => setEarly(e.target.checked)} />
              Verify before the notice period ends
            </label>
          )}
          <Field label={inNotice && early ? 'Note, with the reason for verifying early' : 'Note'}>
            <textarea rows={3} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </>
      )}

      {approvable && !secondPersonNeeded && unsent && (
        <>
          <h2 className="section">Approval</h2>
          <Field label="Reason to approve without notice">
            <textarea rows={2} value={approveNote} maxLength={1000} onChange={(e) => setApproveNote(e.target.value)} />
          </Field>
        </>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------ nominees */

function Nominees() {
  const canHandle = can(getUser(), 'requests:handle');
  const [tab, setTab] = useState<NomineeTab>('registered');
  const [reload, setReload] = useState(0);
  const [contactFor, setContactFor] = useState<Nomination | null>(null);
  const [revoking, setRevoking] = useState<Nomination | null>(null);

  const data = useResource<{ nominations: Nomination[] }>(() => api.get('/v1/nominations'), [reload]);
  const all = data.data?.nominations ?? [];
  const statuses = NOMINEE_TABS.find(([k]) => k === tab)![2];
  const rows = all.filter((n) => statuses.includes(n.status));
  const live = all.filter((n) => n.status === 'registered' || n.status === 'active');
  const people = new Set(live.map((n) => n.principalHash)).size;
  const count = (k: NomineeTab) => all.filter((n) => NOMINEE_TABS.find(([t]) => t === k)![2].includes(n.status)).length;

  return (
    <>
      {data.data && (
        <p className="page-line">
          <strong>{number(people)}</strong> people with a nominee · {number(live.length)} nominees · {number(count('active'))} acting
        </p>
      )}

      <ErrorNote error={data.error} />

      <div className="tabs">
        {NOMINEE_TABS.map(([k, label]) => {
          const n = k === 'ended' ? 0 : count(k);
          return (
            <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
              {label}
              {n ? <span className="tab-count">{n}</span> : null}
            </button>
          );
        })}
      </div>

      <Card>
        {data.loading && !data.data && <Loading what="Loading nominees" />}
        {data.data && rows.length === 0 && <Empty>None.</Empty>}
        {rows.length > 0 && (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr><th>Principal</th><th>Relationship</th><th className="num">Rank</th><th>Contact</th><th>Registered</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {rows.map((n) => (
                  <tr key={n.id}>
                    <td><Link to={`/principals/${n.principalHash}`}><Hash value={n.principalHash} /></Link></td>
                    <td>{RELATIONSHIP_LABEL[n.relationship] ?? sentence(n.relationship)}</td>
                    <td className="num">{n.rank}</td>
                    <td className="mono small">{n.nomineeContact || '—'}</td>
                    <td className="small">{day(n.registeredAt)}</td>
                    <td><Pill tone={NOMINATION_TONE[n.status]}>{NOMINATION_LABEL[n.status] ?? sentence(n.status)}</Pill></td>
                    <td className="u-actions">
                      <RowActions
                        more={canHandle ? [
                          ...(can(getUser(), 'principals:reveal') ? [{ label: 'Show contact', onClick: () => setContactFor(n) }] : []),
                          ...(n.status === 'registered' || n.status === 'active'
                            ? [{ label: 'Revoke', danger: true, onClick: () => setRevoking(n) }] : []),
                        ] : []}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {contactFor && <ContactDialog nomination={contactFor} onClose={() => setContactFor(null)} />}

      {revoking && (
        <ReasonDialog
          title={`Revoke nominee · ${RELATIONSHIP_LABEL[revoking.relationship] ?? sentence(revoking.relationship)} · rank ${revoking.rank}`}
          action="Revoke"
          path={`/v1/nominations/${revoking.id}/revoke`}
          done="Revoked."
          onClose={() => setRevoking(null)}
          onDone={() => { setRevoking(null); setReload((x) => x + 1); }}
        />
      )}
    </>
  );
}

/** Masked until the user gives a reason; then visible to them for five minutes. */
function ContactDialog({ nomination: n, onClose }: { nomination: Nomination; onClose: () => void }) {
  const reveal = useReveal('nomination', n.id);
  return (
    <Modal
      title={`Nominee · ${RELATIONSHIP_LABEL[n.relationship] ?? sentence(n.relationship)} · rank ${n.rank}`}
      onClose={onClose}
      footer={<button onClick={onClose}>Close</button>}
    >
      <dl className="kv">
        <dt>Name</dt><dd>{reveal.values?.nomineeName || <span className="mono muted">Hidden</span>}</dd>
        <dt>{n.nomineeContactType === 'phone' ? 'Mobile number' : 'Email address'}</dt>
        <dd><RevealedField reveal={reveal} value={reveal.values?.nomineeContact} masked={n.nomineeContact} /></dd>
      </dl>
      {reveal.dialog}
      <p className="small muted nm-audit">Each reveal is recorded in the audit log with its reason</p>
    </Modal>
  );
}

/* ------------------------------------------------------------ shared */

function ReasonDialog({ title, action, path, done, onClose, onDone }: {
  title: string; action: string; path: string; done: string; onClose: () => void; onDone: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      await api.post(path, { reason: reason.trim() });
      toast(done, 'ok');
      onDone();
    } catch (err) {
      toast((err as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy}>Cancel</button>
          <button className="danger" onClick={submit} disabled={busy || !reason.trim()}>{busy ? `${action}…` : action}</button>
        </>
      }
    >
      <Field label="Reason">
        <textarea rows={3} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}
